import React from "react";
import Tooltip from "../../../../../components/common/Tooltip/Tooltip";
import { presetTip } from "./presetTip";
import "./AiMaskPaintPreview.css";

const BRUSH_TIP = presetTip('Brush — "How to use"', [
  "Select Brush, then paint on the preview image",
  "Cover what you want removed (red overlay)",
  "Adjust Brush size for fine or large areas",
  "Generate when the mask covers the target",
]);

const ERASER_TIP = presetTip('Eraser — "How to use"', [
  "Select Eraser to remove mask strokes",
  "Paint over red areas you marked by mistake",
  "Use a smaller size for precise cleanup",
  "Switch back to Brush to continue marking",
]);

/**
 * Shared Paint tool + Brush size + Clear mask controls.
 */
const AiMaskPaintControls = ({
  paintTool,
  setPaintTool,
  brushSize,
  setBrushSize,
  brushSizeDraft,
  setBrushSizeDraft,
  brushSizeMin = 8,
  brushSizeMax = 100,
  brushSliderBg,
  hasMask,
  clearMask,
  busy = false,
  sourceImageUrl = null,
  leftPanelRef = null,
  showBrushHint = false,
  brushHint = "Paint over the area on the preview, then generate.",
  /** When false, host renders its own tool picker (e.g. Smart/Brush/Box/Eraser). */
  showToolPicker = true,
  /** Hide size slider for Smart / non-paint modes. */
  showBrushSize = true,
}) => (
  <>
    {showToolPicker ? (
      <div className="aftp-section">
        <p className="aftp-label">Paint tool</p>
        <div className="romp-tools" role="group" aria-label="Paint tool">
          <Tooltip
            content={BRUSH_TIP}
            position="top"
            size="panel"
            block
            flip={false}
            fillBoundary
            boundaryRef={leftPanelRef}
          >
            <button
              type="button"
              className={`romp-tool-btn${paintTool === "brush" ? " is-selected" : ""}`}
              disabled={busy || !sourceImageUrl}
              onClick={() => setPaintTool("brush")}
            >
              <span className="romp-tool-btn__title">Brush</span>
              <span className="romp-tool-btn__sub">Mark remove</span>
            </button>
          </Tooltip>
          <Tooltip
            content={ERASER_TIP}
            position="top"
            size="panel"
            block
            flip={false}
            fillBoundary
            boundaryRef={leftPanelRef}
          >
            <button
              type="button"
              className={`romp-tool-btn${paintTool === "eraser" ? " is-selected" : ""}`}
              disabled={busy || !sourceImageUrl}
              onClick={() => setPaintTool("eraser")}
            >
              <span className="romp-tool-btn__title">Eraser</span>
              <span className="romp-tool-btn__sub">Fix strokes</span>
            </button>
          </Tooltip>
        </div>
      </div>
    ) : null}

    {showBrushSize ? (
      <div className="aftp-section">
        <div className="romp-slider-field">
          <div className="romp-slider-field__head">
            <span className="romp-slider-field__label">
              {paintTool === "eraser" ? "Eraser size" : "Brush size"}
            </span>
            <div className="romp-slider-field__value">
              <div className="romp-slider-field__value-wrap">
                <input
                  type="text"
                  inputMode="numeric"
                  className="romp-slider-field__input"
                  value={brushSizeDraft ?? brushSize}
                  disabled={busy || !sourceImageUrl}
                  aria-label="Brush size value"
                  onFocus={() => setBrushSizeDraft(String(brushSize))}
                  onChange={(e) => {
                    const raw = String(e.target.value).replace(/[^\d]/g, "");
                    setBrushSizeDraft(raw);
                    const n = Number(raw);
                    if (
                      Number.isFinite(n) &&
                      n >= brushSizeMin &&
                      n <= brushSizeMax
                    ) {
                      setBrushSize(n);
                    }
                  }}
                  onBlur={() => {
                    const n = Number(brushSizeDraft);
                    if (Number.isFinite(n)) {
                      setBrushSize(
                        Math.min(brushSizeMax, Math.max(brushSizeMin, n))
                      );
                    }
                    setBrushSizeDraft(null);
                  }}
                />
              </div>
              <span className="romp-slider-field__suffix">px</span>
            </div>
          </div>
          <input
            type="range"
            className="romp-slider"
            min={brushSizeMin}
            max={brushSizeMax}
            step={1}
            value={brushSize}
            disabled={busy || !sourceImageUrl}
            style={{ "--romp-slider-bg": brushSliderBg }}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            aria-label="Brush size"
          />
        </div>
      </div>
    ) : null}

    <button
      type="button"
      className="romp-clear-btn"
      disabled={busy || !hasMask}
      onClick={() => {
        clearMask();
      }}
    >
      Clear mask
    </button>

    {showBrushHint ? <p className="romp-hint">{brushHint}</p> : null}
  </>
);

export default AiMaskPaintControls;
